import {
  Button,
  Card,
  Flex,
  Group,
  Menu,
  Modal,
  Select,
  Text,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconClock, IconDotsVertical } from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useRef, useState } from 'react';
import { BalanceText } from '../../components/balance-text';
import { CustomLoader } from '../../components/custom-loader';
import { Page } from '../../components/page';
import { showBankHour } from '../../data/bank-hours';
import { getUserFiltersRequest } from '../../data/filters';
import {
  useApprovalBankHourRequest,
  useGetBankHoursRequestPaginated,
} from '../../data/hooks/bank-hours-request';
import { useAuth } from '../../hooks/useAuth';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  BankHourRequest,
  BankHourRequestStatus,
} from '../../models/bank-hours-request';
import { UserRole, UserType } from '../../models/user';
import { formatLocale } from '../../providers/dayjs-plugins';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import {
  BankHourRequestStatusHumanized,
  bankHourRequestStatusList,
} from '../../utils/constants/bank-hour';
import { formatHours, formatSignedHours } from '../../utils/helpers';
import { GetFilterUsersResponse } from '../../utils/types/data/services/filters';

export type PageModalState = 'review-bank-hours' | null;

type ReviewBankHourFormType = {
  isApproval: boolean;
  note: string;
};

export function RequestBankHoursList() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [userList, setUserList] = useState<GetFilterUsersResponse[]>([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageModal, setPageModal] = useState<PageModalState>(null);
  const [selectedRequestBankHour, setSelectedRequestBankHour] =
    useState<BankHourRequest>();
  // requester balance in seconds; undefined = unknown (no permission / error)
  const [requesterBalance, setRequesterBalance] = useState<number>();

  const page = useRef(1);
  const limit = useRef(10);

  const formFilter = useForm({
    initialValues: {
      userId: '',
      status: '',
    },
  });

  const reviewBankHourForm = useForm<ReviewBankHourFormType>({
    initialValues: {
      isApproval: false,
      note: '',
    },
  });

  const {
    fetch: getBankHourRequestPaginatedFetcher,
    loading: getBankHourRequestPaginatedLoader,
    response: getBankHourRequestPaginatedResponse,
  } = useGetBankHoursRequestPaginated();

  const { fetch: requestApprovalFetcher, loading: requestApprovalLoader } =
    useApprovalBankHourRequest();

  async function handlePageChange(newPage: number) {
    page.current = newPage;
    await getBankHourRequests();
  }

  function handleClearFilter() {
    formFilter.reset();
    getBankHourRequests(true);
  }

  function handlePageLimitChange(limitValue: number) {
    limit.current = limitValue;
    getBankHourRequests();
  }

  async function getBankHourRequests(reset = false) {
    const validatedValues: any = {};

    Object.entries(formFilter.values).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        validatedValues[key] = val;
      }
    });

    if (reset) {
      await getBankHourRequestPaginatedFetcher({
        params: {
          page: page.current,
          limit: limit.current,
          supervisorId:
            user?.user.type === UserType.MANAGER ? user.user.id : undefined,
        },
      });
    } else {
      await getBankHourRequestPaginatedFetcher({
        params: {
          page: page.current,
          limit: limit.current,
          supervisorId:
            user?.user.type === UserType.MANAGER ? user.user.id : undefined,
          ...validatedValues,
        },
      });
    }
  }

  async function getUsers() {
    try {
      setPageLoading(true);
      const response = await getUserFiltersRequest({
        role: UserRole.EMPLOYEE,
      });
      setUserList(response);
      setPageLoading(false);
    } catch (error) {
      setPageLoading(false);
      errorNotification({
        title: 'Erro ao buscar usuários.',
        message: 'tente novamente',
      });
    }
  }

  function handleOpenModal(
    bankRequest: BankHourRequest,
    modal: PageModalState,
  ) {
    setSelectedRequestBankHour(bankRequest);
    setRequesterBalance(undefined);
    setPageModal(modal);

    showBankHour({ user: bankRequest.requestUser })
      .then((res) =>
        setRequesterBalance(res.data.amount + res.data.dailyWorkAmount),
      )
      .catch(() => setRequesterBalance(undefined));
  }

  async function handleApprovalBankHour(
    values: typeof reviewBankHourForm.values,
  ) {
    if (!selectedRequestBankHour) {
      errorNotification({
        message: 'Selecione uma solicitação de banco de horas',
        title: 'tente novamente',
      });
      return;
    }
    await requestApprovalFetcher({
      data: {
        isApproved: values.isApproval,
        supervisorNote: values.note.trim() || undefined,
      },
      requestId: selectedRequestBankHour?.id,
      onSuccess: () => {
        setSelectedRequestBankHour(undefined);
        setRequesterBalance(undefined);
        reviewBankHourForm.reset();
        getBankHourRequests();
        setPageModal(null);
        successNotification({
          message: 'Solicitação avaliada com successo!',
          title: 'Tudo certo',
        });
      },
    });
  }

  // only the RH really debits the balance; the supervisor just pre-approves
  const notEnoughBalance =
    user?.user.type !== UserType.MANAGER &&
    requesterBalance !== undefined &&
    requesterBalance < (selectedRequestBankHour?.amount ?? 0);

  useEffect(() => {
    getBankHourRequests();
    getUsers();
  }, []);

  return (
    <Page title="Solicitações de Banco de horas">
      <CustomLoader loading={pageLoading} />
      <Flex wrap="wrap" justify="start" mt={'lg'}>
        <form onSubmit={formFilter.onSubmit(() => getBankHourRequests())}>
          <Flex align="center">
            <Select
              clearable
              name="user"
              label="Funcionário"
              placeholder="selecione o funcionário"
              data={userList?.map((item) => ({
                label: item.username,
                value: String(item.id),
              }))}
              {...formFilter.getInputProps('userId')}
              mb={16}
              mr={8}
            />
            <Select
              clearable
              name="status"
              label="Status"
              placeholder="selecione o status"
              data={bankHourRequestStatusList}
              {...formFilter.getInputProps('status')}
              mb={16}
              mr={8}
            />

            <Button mb={-10} type="submit">
              Filtrar
            </Button>
            <Button
              mb={-10}
              ml={10}
              onClick={handleClearFilter}
              color="ltpBlue.9"
              variant="outline"
            >
              Limpar
            </Button>
          </Flex>
        </form>
      </Flex>
      <DataTable
        recordsPerPage={
          getBankHourRequestPaginatedResponse?.meta.itemsPerPage ?? 10
        }
        recordsPerPageOptions={[10, 50, 100, 500]}
        onRecordsPerPageChange={(recordRange) =>
          handlePageLimitChange(recordRange)
        }
        onPageChange={handlePageChange}
        totalRecords={getBankHourRequestPaginatedResponse?.meta.totalItems}
        page={page.current}
        records={getBankHourRequestPaginatedResponse?.items ?? []}
        noRecordsText="Sem registros"
        withBorder={false}
        borderRadius="sm"
        striped
        highlightOnHover
        height="66vh"
        fetching={getBankHourRequestPaginatedLoader}
        columns={[
          { accessor: 'requestUsername', title: 'Funcionário' },
          {
            accessor: 'status',
            title: 'Status',
            width: 300,
            render: ({ status }) => BankHourRequestStatusHumanized[status],
          },
          {
            accessor: 'amount',
            title: 'Horas',
            render: ({ amount }) => {
              return <Text fw="bold">{formatHours(amount / 60)}</Text>;
            },
          },
          {
            accessor: 'dateBegin',
            title: 'Início',
            render: ({ dateBegin }) => formatLocale(dateBegin, 'DD/MM/YY'),
          },
          {
            accessor: 'dateEnd',
            title: 'Fim',
            render: ({ dateEnd }) => formatLocale(dateEnd, 'DD/MM/YY'),
          },
          {
            accessor: 'action',
            title: '',
            render: (item) => (
              <Menu>
                <Menu.Target>
                  <Button color="blue" variant="subtle" w={40} p={0}>
                    <IconDotsVertical />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown style={{ position: 'absolute' }}>
                  <Menu.Item
                    icon={<IconClock size={14} />}
                    onClick={() => handleOpenModal(item, 'review-bank-hours')}
                    disabled={
                      (user?.user.type === UserType.MANAGER &&
                        [
                          BankHourRequestStatus.SUPERVISOR_APPROVED,
                          BankHourRequestStatus.RH_APPROVED,
                        ].includes(item.status)) ||
                      [
                        BankHourRequestStatus.RH_APPROVED,
                        BankHourRequestStatus.REPROVED,
                        BankHourRequestStatus.CANCELED,
                      ].includes(item.status)
                    }
                  >
                    Avaliar
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ),
          },
        ]}
      />

      <Modal
        fullScreen={isMobile}
        title="Avaliar Solicitação de Banco de Horas"
        opened={pageModal === 'review-bank-hours'}
        onClose={() => setPageModal(null)}
        closeOnClickOutside={false}
        withCloseButton={true}
        closeOnEscape={true}
        size={'xl'}
      >
        <form onSubmit={reviewBankHourForm.onSubmit(handleApprovalBankHour)}>
          <Card bg="#f7cb7340" mb={8} radius={'md'}>
            <Flex justify={'space-between'} align="center">
              <Text fw={'bold'}>
                {selectedRequestBankHour?.requestUsername}
              </Text>
            </Flex>
            <Text>{selectedRequestBankHour?.description}</Text>
            <Text size="sm">
              {formatLocale(
                selectedRequestBankHour?.dateBegin ?? '',
                'DD/MM/YY',
              )}
              -{' '}
              {formatLocale(selectedRequestBankHour?.dateEnd ?? '', 'DD/MM/YY')}
            </Text>
            <Text size="sm">
              {selectedRequestBankHour?.status &&
                BankHourRequestStatusHumanized[selectedRequestBankHour?.status]}
            </Text>

            <Flex gap="xl" mt="md">
              <Flex direction="column">
                <Text size="xs" color="dimmed">
                  Horas solicitadas
                </Text>
                <Text fw="bold">
                  {formatHours((selectedRequestBankHour?.amount ?? 0) / 60)}
                </Text>
              </Flex>
              <Flex direction="column">
                <Text size="xs" color="dimmed">
                  Saldo atual do funcionário
                </Text>
                {requesterBalance !== undefined ? (
                  <BalanceText
                    value={requesterBalance}
                    formatted={formatSignedHours(requesterBalance)}
                  />
                ) : (
                  <Text fw="bold">--</Text>
                )}
              </Flex>
            </Flex>
            {notEnoughBalance && (
              <Text color="red" size="sm" fw="bold" mt="sm">
                O saldo não cobre as horas solicitadas: o RH não consegue
                aprovar este pedido.
              </Text>
            )}

            <Flex direction={'column'} mt={'xl'}>
              <Text size={'sm'} color="dimmed" fw={'bold'}>
                Observação supervisor
              </Text>
              <Text size="sm">{selectedRequestBankHour?.supervisorNote}</Text>
            </Flex>
          </Card>

          <Textarea
            label="Sua observação"
            description="Opcional. O funcionário vê este texto na solicitação."
            placeholder="ex: Aprovado para a sexta, combinado com a equipe"
            maxLength={200}
            autosize
            minRows={2}
            {...reviewBankHourForm.getInputProps('note')}
          />

          <Group position="right" mt={'md'}>
            <Button
              type="submit"
              color="red"
              onClick={() =>
                reviewBankHourForm.setFieldValue('isApproval', false)
              }
              loading={requestApprovalLoader}
            >
              Reprovar
            </Button>
            <Button
              type="submit"
              color="green"
              onClick={() =>
                reviewBankHourForm.setFieldValue('isApproval', true)
              }
              loading={requestApprovalLoader}
              disabled={notEnoughBalance}
            >
              Aprovar
            </Button>
          </Group>
        </form>
      </Modal>
    </Page>
  );
}
